import {
    ActionIcon,
    Button,
    Group,
    NumberInput,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@mantine/core'
import type {UseFormReturnType} from '@mantine/form'
import {IconPlus, IconTrash} from '@tabler/icons-react'
import {type ReactNode} from 'react'

import {parseNumberInputValue} from '../lib/parse-number-input-value'
import {BelowMinimumRateAcknowledgement} from '../projects/billing-terms-below-minimum-rate-warning'
import {type CreateProjectFormValues} from '../projects/create-project-form-values'
import type {ProjectBillingMethod} from '../projects/schemas'
import {useTransportationRatePolicy} from '../settings/use-transportation-rate-policy'
import {ORDER_BILLING_CHOICES} from './order-billing'
import {
    type BillingTermsFormValues,
    createEmptyManualBillableLineItem,
    type DisposalFeeInput,
    type ManualBillableLineItemInput,
} from './types'

type StepBillingTermsProps = {
    form: UseFormReturnType<CreateProjectFormValues>
    showManualCharges?: boolean
    methodNote?: ReactNode
}

const DISPOSAL_UNIT_OPTIONS = [
    {value: 'gallon', label: 'Gallon'},
    {value: 'barrel', label: 'Barrel'},
    {value: 'ton', label: 'Ton'},
    {value: 'totes', label: 'Totes'},
    {value: 'each', label: 'Each'},
    {value: 'load', label: 'Load'},
]

function BillingMethodCard({
    selected,
    title,
    description,
    onSelect,
}: {
    selected: boolean
    title: string
    description: string
    onSelect: () => void
}) {
    return (
        <Paper
            withBorder
            p="md"
            radius="md"
            role="button"
            aria-pressed={selected}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect()
                }
            }}
            style={{
                cursor: 'pointer',
                borderColor: selected ? 'var(--mantine-color-blue-6)' : undefined,
                backgroundColor: selected ? 'var(--mantine-color-blue-0)' : undefined,
            }}
        >
            <Stack gap="xs">
                <Text fw={600}>{title}</Text>
                <Text size="sm" c="dimmed">
                    {description}
                </Text>
            </Stack>
        </Paper>
    )
}

function DisposalFeeRow({
    fee,
    index,
    onChange,
    onRemove,
    errors,
}: {
    fee: DisposalFeeInput
    index: number
    onChange: (patch: Partial<DisposalFeeInput>) => void
    onRemove: () => void
    errors: Record<string, ReactNode>
}) {
    return (
        <Paper withBorder p="sm" radius="md">
            <Stack gap="sm">
                <Group justify="space-between">
                    <Text size="sm" fw={500}>
                        Disposal fee {index + 1}
                    </Text>
                    <ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="Remove">
                        <IconTrash size={14} />
                    </ActionIcon>
                </Group>
                <SimpleGrid cols={{base: 1, sm: 2}}>
                    <TextInput
                        label="Waste type"
                        value={fee.wasteType}
                        onChange={(event) => onChange({wasteType: event.currentTarget.value})}
                        error={errors[`billingTerms.disposalFees.${index}.wasteType`]}
                    />
                    <Select
                        label="Unit"
                        data={DISPOSAL_UNIT_OPTIONS}
                        value={fee.unit}
                        onChange={(value) => onChange({unit: value ?? ''})}
                        error={errors[`billingTerms.disposalFees.${index}.unit`]}
                    />
                </SimpleGrid>
                <SimpleGrid cols={{base: 1, sm: 2}}>
                    <NumberInput
                        label="Unit rate"
                        min={0}
                        prefix="$"
                        decimalScale={4}
                        value={fee.unitRate ?? ''}
                        onChange={(value) => onChange({unitRate: parseNumberInputValue(value)})}
                        error={errors[`billingTerms.disposalFees.${index}.unitRate`]}
                    />
                    <NumberInput
                        label="Estimated quantity"
                        min={0}
                        decimalScale={2}
                        value={fee.estimatedQuantity ?? ''}
                        onChange={(value) =>
                            onChange({estimatedQuantity: parseNumberInputValue(value)})
                        }
                        error={errors[`billingTerms.disposalFees.${index}.estimatedQuantity`]}
                    />
                </SimpleGrid>
            </Stack>
        </Paper>
    )
}

function ManualLineItemRow({
    lineItem,
    index,
    onChange,
    onRemove,
    errors,
}: {
    lineItem: ManualBillableLineItemInput
    index: number
    onChange: (patch: Partial<ManualBillableLineItemInput>) => void
    onRemove: () => void
    errors: Record<string, ReactNode>
}) {
    return (
        <Paper withBorder p="sm" radius="md">
            <Stack gap="sm">
                <Group justify="space-between">
                    <Text size="sm" fw={500}>
                        Manual charge {index + 1}
                    </Text>
                    <ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="Remove">
                        <IconTrash size={16} />
                    </ActionIcon>
                </Group>
                <SimpleGrid cols={2}>
                    <TextInput
                        label="Name"
                        value={lineItem.name}
                        onChange={(event) => onChange({name: event.currentTarget.value})}
                        error={errors[`manualBillableLineItems.${index}.name`]}
                    />
                    <TextInput
                        label="Unit"
                        value={lineItem.unit}
                        onChange={(event) => onChange({unit: event.currentTarget.value})}
                        error={errors[`manualBillableLineItems.${index}.unit`]}
                    />
                </SimpleGrid>
                <Textarea
                    label="Description"
                    value={lineItem.description}
                    onChange={(event) => onChange({description: event.currentTarget.value})}
                />
                <SimpleGrid cols={2}>
                    <NumberInput
                        label="Quantity"
                        min={0}
                        decimalScale={2}
                        value={lineItem.quantity ?? ''}
                        onChange={(value) => onChange({quantity: parseNumberInputValue(value)})}
                        error={errors[`manualBillableLineItems.${index}.quantity`]}
                    />
                    <NumberInput
                        label="Unit price"
                        min={0}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        value={lineItem.unitPrice ?? ''}
                        onChange={(value) => onChange({unitPrice: parseNumberInputValue(value)})}
                        error={errors[`manualBillableLineItems.${index}.unitPrice`]}
                    />
                </SimpleGrid>
            </Stack>
        </Paper>
    )
}

export function StepBillingTerms({
    form,
    showManualCharges = true,
    methodNote,
}: StepBillingTermsProps) {
    const {billingMethod, billingTerms, manualBillableLineItems} = form.values
    const disposalFees = billingTerms.disposalFees
    const {
        data: policy,
        error: policyError,
        isLoading: policyIsLoading,
    } = useTransportationRatePolicy()

    function setBillingMethod(method: ProjectBillingMethod) {
        form.setFieldValue('billingMethod', method)
    }

    function setBillingTermsField<K extends keyof BillingTermsFormValues>(
        field: K,
        value: BillingTermsFormValues[K],
    ) {
        form.setFieldValue('billingTerms', {...billingTerms, [field]: value})
    }

    function addManualLineItem() {
        form.setFieldValue('manualBillableLineItems', [
            ...manualBillableLineItems,
            createEmptyManualBillableLineItem(),
        ])
    }

    function updateManualLineItem(index: number, patch: Partial<ManualBillableLineItemInput>) {
        form.setFieldValue(
            'manualBillableLineItems',
            manualBillableLineItems.map((lineItem, lineIndex) =>
                lineIndex === index ? {...lineItem, ...patch} : lineItem,
            ),
        )
    }

    function removeManualLineItem(index: number) {
        form.setFieldValue(
            'manualBillableLineItems',
            manualBillableLineItems.filter((_, lineIndex) => lineIndex !== index),
        )
    }

    function addDisposalFee() {
        form.setFieldValue('billingTerms.disposalFees', [
            ...disposalFees,
            {
                wasteType: '',
                unit: 'gallon',
                unitRate: null,
                estimatedQuantity: null,
            },
        ])
    }

    function updateDisposalFee(index: number, patch: Partial<DisposalFeeInput>) {
        form.setFieldValue(
            'billingTerms.disposalFees',
            disposalFees.map((fee, feeIndex) => (feeIndex === index ? {...fee, ...patch} : fee)),
        )
    }

    function removeDisposalFee(index: number) {
        form.setFieldValue(
            'billingTerms.disposalFees',
            disposalFees.filter((_, feeIndex) => feeIndex !== index),
        )
    }

    return (
        <Stack gap="lg">
            <Stack gap="xs">
                <Title order={4}>Billing method</Title>
                <SimpleGrid cols={{base: 1, sm: 2, lg: 4}}>
                    {ORDER_BILLING_CHOICES.map((choice) => (
                        <BillingMethodCard
                            key={choice.method}
                            selected={billingMethod === choice.method}
                            title={choice.title}
                            description={choice.description}
                            onSelect={() => setBillingMethod(choice.method)}
                        />
                    ))}
                </SimpleGrid>
                {methodNote}
            </Stack>

            {billingMethod === 'line_rates' ? null : billingMethod === 'hourly_transportation' ? (
                <NumberInput
                    label="All-in hourly rate"
                    min={0}
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale
                    value={billingTerms.transportationHourlyRate ?? ''}
                    onChange={(value) =>
                        setBillingTermsField(
                            'transportationHourlyRate',
                            parseNumberInputValue(value),
                        )
                    }
                    error={form.errors['billingTerms.transportationHourlyRate']}
                />
            ) : billingMethod === 'per_load' ? (
                <SimpleGrid cols={{base: 1, sm: 2}}>
                    <NumberInput
                        label="Rate per load"
                        min={0}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        value={billingTerms.ratePerLoad ?? ''}
                        onChange={(value) =>
                            setBillingTermsField('ratePerLoad', parseNumberInputValue(value))
                        }
                        error={form.errors['billingTerms.ratePerLoad']}
                    />
                    <TextInput
                        label="Per-load notes"
                        placeholder="What counts as a load (optional)"
                        value={billingTerms.perLoadNotes ?? ''}
                        onChange={(event) =>
                            setBillingTermsField('perLoadNotes', event.currentTarget.value)
                        }
                    />
                </SimpleGrid>
            ) : (
                <NumberInput
                    label="Flat rate amount"
                    min={0}
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale
                    value={billingTerms.flatRateAmount ?? ''}
                    onChange={(value) =>
                        setBillingTermsField('flatRateAmount', parseNumberInputValue(value))
                    }
                    error={form.errors['billingTerms.flatRateAmount']}
                />
            )}

            <BelowMinimumRateAcknowledgement
                form={form}
                activeMinimumHourlyRate={policy?.minimumHourlyRate ?? null}
                policyError={policyError ?? null}
                policyIsLoading={policyIsLoading}
            />

            <Stack gap="sm">
                <Title order={4}>Quoted extras</Title>

                {billingMethod !== 'hourly_transportation' ? (
                    <NumberInput
                        label="Demurrage hourly rate"
                        min={0}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        value={billingTerms.demurrageHourlyRate ?? ''}
                        onChange={(value) =>
                            setBillingTermsField(
                                'demurrageHourlyRate',
                                parseNumberInputValue(value),
                            )
                        }
                    />
                ) : null}
                <Stack gap="sm">
                    <Group justify="space-between">
                        <Title order={4}>Disposal fees</Title>
                        <Button
                            type="button"
                            variant="light"
                            size="xs"
                            leftSection={<IconPlus size={14} />}
                            onClick={addDisposalFee}
                        >
                            Add fee
                        </Button>
                    </Group>
                    {disposalFees.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No disposal fees added.
                        </Text>
                    ) : (
                        disposalFees.map((fee, index) => (
                            <DisposalFeeRow
                                key={index}
                                fee={fee}
                                index={index}
                                onChange={(patch) => updateDisposalFee(index, patch)}
                                onRemove={() => removeDisposalFee(index)}
                                errors={form.errors}
                            />
                        ))
                    )}
                </Stack>
                <SimpleGrid cols={{base: 1, sm: 2}}>
                    <NumberInput
                        label="Washout fee"
                        min={0}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        value={billingTerms.washoutFee ?? ''}
                        onChange={(value) =>
                            setBillingTermsField('washoutFee', parseNumberInputValue(value))
                        }
                    />
                    <NumberInput
                        label="HSEI percent"
                        min={0}
                        suffix="%"
                        decimalScale={2}
                        value={billingTerms.hseiPercent ?? ''}
                        onChange={(value) =>
                            setBillingTermsField('hseiPercent', parseNumberInputValue(value))
                        }
                    />
                </SimpleGrid>
            </Stack>

            {showManualCharges ? (
                <Stack gap="sm">
                    <Group justify="space-between">
                        <Title order={4}>Manual charges</Title>
                        <Button
                            type="button"
                            variant="light"
                            size="xs"
                            leftSection={<IconPlus size={14} />}
                            onClick={addManualLineItem}
                        >
                            Add charge
                        </Button>
                    </Group>
                    {manualBillableLineItems.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            No manual charges added.
                        </Text>
                    ) : (
                        manualBillableLineItems.map((lineItem, index) => (
                            <ManualLineItemRow
                                key={index}
                                lineItem={lineItem}
                                index={index}
                                onChange={(patch) => updateManualLineItem(index, patch)}
                                onRemove={() => removeManualLineItem(index)}
                                errors={form.errors}
                            />
                        ))
                    )}
                </Stack>
            ) : null}
        </Stack>
    )
}
